import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Upload,
  Download as DownloadIcon,
  Close as CloseIcon,
  Delete,
  EmojiEvents,
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import toast from 'react-hot-toast';
import resultService from '../../services/resultService';
import schoolService from '../../services/schoolService';
import { Result } from '../../types';
import CertificatePdf from './CertificatePdf';

const AWARD_COLORS: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  'NATIONAL LEVEL MERIT': 'warning',
  'ZONAL LEVEL MERIT': 'info',
  'SCHOOL LEVEL MERIT': 'success',
  'PARTICIPATION': 'default',
};

const CertificatesPage: React.FC = () => {
  const { schoolId } = useParams<{ schoolId: string }>();
  const location = useLocation();
  const [schoolName, setSchoolName] = useState<string>(location.state?.schoolName || '');

  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterGrade, setFilterGrade] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterAwarded, setFilterAwarded] = useState('');

  // PDF state
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (schoolId) {
      fetchResults();
      if (!schoolName) {
        schoolService.getSchoolById(schoolId).then((res) => {
          setSchoolName(res.data?.name || 'School');
        }).catch(() => {});
      }
    }
  }, [schoolId]);

  const fetchResults = async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await resultService.getResultsBySchool(schoolId);
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !schoolId) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    setError(null);
    try {
      const imported = await resultService.importCsv(file, schoolId);
      toast.success(`Imported ${imported.length} results`);
      await fetchResults();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteResults = async () => {
    if (!schoolId) return;
    const confirmed = window.confirm(
      `Delete all ${results.length} results for this school? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await resultService.deleteResultsBySchool(schoolId);
      setResults([]);
      setPdfUrl(null);
      toast.success('Results deleted');
    } catch (err) {
      toast.error('Failed to delete results');
    }
  };

  // Derived filter options
  const grades = Array.from(new Set(results.map((r) => r.grade))).sort();
  const subjects = Array.from(new Set(results.map((r) => r.subject))).sort();
  const awardedValues = Array.from(new Set(results.map((r) => r.awarded))).sort();

  // Filtered results
  const filteredResults = results.filter((r) => {
    if (filterGrade && r.grade !== filterGrade) return false;
    if (filterSubject && r.subject !== filterSubject) return false;
    if (filterAwarded && r.awarded !== filterAwarded) return false;
    return true;
  });

  // PDF generation
  const handlePreviewCertificates = async () => {
    if (filteredResults.length === 0) return;
    setPdfPreviewOpen(true);

    if (pdfUrl) return; // Already cached

    setPdfGenerating(true);
    setPdfProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setPdfProgress((prev) => Math.min(prev + 5, 90));
      }, 300);

      const pdfDoc = (
        <CertificatePdf results={filteredResults} schoolName={schoolName} />
      );
      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      clearInterval(progressInterval);
      setPdfUrl(url);
      setPdfProgress(100);
      setTimeout(() => setPdfGenerating(false), 500);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setPdfGenerating(false);
      setPdfProgress(0);
      toast.error('Failed to generate PDF');
    }
  };

  const handleDownloadCertificates = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `Certificates - ${schoolName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClosePreview = () => {
    setPdfPreviewOpen(false);
    setPdfGenerating(false);
    setPdfProgress(0);
  };

  // Invalidate PDF cache when filters change
  useEffect(() => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [filterGrade, filterSubject, filterAwarded]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Certificates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {schoolName} - Academic Year 2025-26
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={importing ? <CircularProgress size={16} /> : <Upload />}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Upload CSV'}
            <input type="file" hidden accept=".csv" onChange={handleCsvUpload} />
          </Button>
          {results.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteResults}
            >
              Clear Results
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {results.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{results.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Results</Typography>
            </CardContent>
          </Card>
          {awardedValues.map((awarded) => (
            <Card key={awarded}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {results.filter((r) => r.awarded === awarded).length}
                </Typography>
                <Chip
                  label={awarded}
                  size="small"
                  color={AWARD_COLORS[awarded] || 'default'}
                  sx={{ mt: 0.5 }}
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Filters */}
      {results.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Grade</InputLabel>
            <Select value={filterGrade} label="Grade" onChange={(e) => setFilterGrade(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {grades.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Subject</InputLabel>
            <Select value={filterSubject} label="Subject" onChange={(e) => setFilterSubject(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {subjects.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Award Level</InputLabel>
            <Select value={filterAwarded} label="Award Level" onChange={(e) => setFilterAwarded(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {awardedValues.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={<EmojiEvents />}
            onClick={handlePreviewCertificates}
            disabled={filteredResults.length === 0}
          >
            Preview Certificates ({filteredResults.length})
          </Button>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No results imported yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload a CSV file with student performance data to generate certificates
            </Typography>
            <Button variant="contained" component="label" startIcon={<Upload />}>
              Upload CSV
              <input type="file" hidden accept=".csv" onChange={handleCsvUpload} />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      {filteredResults.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">%</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Awarded</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.map((result) => (
                <TableRow key={result.id} hover>
                  <TableCell>
                    {result.firstName} {result.lastName}
                  </TableCell>
                  <TableCell>{result.grade}</TableCell>
                  <TableCell>{result.subject}</TableCell>
                  <TableCell align="right">{result.percentage?.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      label={result.awarded}
                      size="small"
                      color={AWARD_COLORS[result.awarded] || 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Certificates - {schoolName} ({filteredResults.length} certificates)
          </Typography>
          <Box>
            {pdfUrl && (
              <IconButton color="primary" onClick={handleDownloadCertificates} sx={{ mr: 1 }}>
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', position: 'relative' }}>
          {pdfGenerating ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: '#f5f5f5',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Generating Certificates... {Math.round(pdfProgress)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Processing {filteredResults.length} certificates
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pdfProgress}
                sx={{ width: '60%', height: 8, borderRadius: 4 }}
              />
            </Box>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#f5f5f5' }}
              title="Certificates Preview"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CertificatesPage;
